import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService, { Product } from '../services/api';
import analytics from '../services/analytics';
import { useToast } from '../contexts/ToastContext';
import { useCart } from '../contexts/CartContext';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c1810 0%, #4a2c1a 100%);
  color: white;
  padding: var(--spacing-16) 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="cottage" patternUnits="userSpaceOnUse" width="50" height="50"><circle cx="10" cy="10" r="1" fill="white" opacity="0.1"/><circle cx="40" cy="25" r="0.5" fill="white" opacity="0.1"/><circle cx="25" cy="40" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23cottage)"/></svg>');
    pointer-events: none;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
    position: relative;
    z-index: 1;
  }

  h1 {
    font-size: 4rem;
    font-weight: 800;
    margin-bottom: var(--spacing-6);
    text-shadow: 2px 2px 8px rgba(0,0,0,0.3);
    line-height: 1.1;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  .subtitle {
    font-size: 1.5rem;
    opacity: 0.95;
    margin-bottom: var(--spacing-8);
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }

  .cta-button {
    background: #d4a574;
    color: white;
    border: none;
    padding: var(--spacing-4) var(--spacing-8);
    border-radius: var(--border-radius-lg);
    font-size: 1.2rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;

    &:hover {
      background: #c19660;
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(212, 165, 116, 0.3);
    }
  }
`;

const ContentSection = styled.section`
  padding: var(--spacing-16) 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
  }

  h2 {
    font-size: 3rem;
    color: #2c1810;
    margin-bottom: var(--spacing-8);
    text-align: center;
  }

  .intro-text {
    font-size: 1.2rem;
    line-height: 1.8;
    color: var(--color-text);
    max-width: 900px;
    margin: 0 auto var(--spacing-12) auto;
    text-align: center;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--spacing-8);
  margin-top: var(--spacing-8);
`;

const ProductCard = styled.div`
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  overflow: hidden;
  transition: all 0.4s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }

  .product-image {
    width: 100%;
    height: 280px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 5rem;
    position: relative;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s ease;
    }

    &:hover img {
      transform: scale(1.05);
    }
  }

  .product-content {
    padding: var(--spacing-6);

    .product-name {
      font-size: 1.4rem;
      font-weight: 700;
      color: #2c1810;
      margin-bottom: var(--spacing-3);
      line-height: 1.3;
    }

    .product-description {
      color: var(--color-text-light);
      line-height: 1.6;
      margin-bottom: var(--spacing-4);
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .product-price {
      display: flex;
      align-items: center;
      gap: var(--spacing-3);
      margin-bottom: var(--spacing-4);

      .current-price {
        font-size: 1.6rem;
        font-weight: 800;
        color: #2c1810;
      }

      .original-price {
        font-size: 1.1rem;
        color: var(--color-text-light);
        text-decoration: line-through;
      }

      .discount-badge {
        background: #dc3545;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 0.8rem;
        font-weight: 600;
        text-transform: uppercase;
      }
    }

    .product-actions {
      display: flex;
      gap: var(--spacing-3);

      .view-btn, .add-btn {
        flex: 1;
        padding: var(--spacing-3) var(--spacing-4);
        border-radius: var(--border-radius);
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        text-align: center;
        font-size: 0.9rem;
      }

      .view-btn {
        background: transparent;
        color: #2c1810;
        border: 2px solid #2c1810;

        &:hover {
          background: #2c1810;
          color: white;
        }
      }

      .add-btn {
        background: #d4a574;
        color: white;
        border: none;

        &:hover {
          background: #c19660;
        }
      }
    }
  }
`;

const AboutSection = styled.section`
  background: white;
  padding: var(--spacing-16) 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
  }

  h2 {
    text-align: center;
    color: #2c1810;
    margin-bottom: var(--spacing-12);
    font-size: 2.8rem;
  }

  .about-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-12);
    align-items: center;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--spacing-6);
    }
  }

  .about-text {
    font-size: 1.1rem;
    line-height: 1.8;
    color: var(--color-text);

    p {
      margin-bottom: var(--spacing-4);
    }
  }

  .features-list {
    list-style: none;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      margin-bottom: var(--spacing-3);
      font-size: 1.1rem;

      &::before {
        content: '🏠';
        margin-right: var(--spacing-3);
        font-size: 1.5rem;
      }
    }
  }
`;

const CallToAction = styled.section`
  background: linear-gradient(135deg, #2c1810 0%, #4a2c1a 100%);
  color: white;
  padding: var(--spacing-16) 0;
  text-align: center;

  .container {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
  }

  h2 {
    font-size: 3rem;
    margin-bottom: var(--spacing-6);
    font-weight: 700;
  }

  p {
    font-size: 1.3rem;
    opacity: 0.9;
    margin-bottom: var(--spacing-8);
    line-height: 1.6;
  }

  .cta-button {
    background: #d4a574;
    color: white;
    border: none;
    padding: var(--spacing-4) var(--spacing-8);
    border-radius: var(--border-radius-lg);
    font-size: 1.2rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;

    &:hover {
      background: #c19660;
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(212, 165, 116, 0.3);
    }
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  font-size: 1.2rem;
  color: var(--color-text-light);
`;

const MajasCottageProducts: React.FC = () => {
  const navigate = useNavigate();
  type MajasProduct = Product & { id: number };

  const { showToast } = useToast();
  const { addItem } = useCart();
  const [products, setProducts] = useState<MajasProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProducts();
    analytics.trackPageView('Majas Cottage Produkter - Premium Inredning | Lillaflora');
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    setError('');

    try {
      // Get all products and filter for Majas Cottage
      const allProducts = await apiService.getProducts();

      // Filter products that contain "Majas" or "Cottage" in name or description
      const majasProducts = allProducts.filter((product: Product) => {
        const searchText = `${product.name} ${product.description} ${product.short_description}`.toLowerCase();
        return searchText.includes('majas') || searchText.includes('cottage');
      });

      const availableProducts = majasProducts.filter(
        (product): product is MajasProduct =>
          typeof product.id === 'number' && (product.inventory_quantity ?? 0) > 0
      );

      setProducts(availableProducts);
    } catch (err: any) {
      console.error('Error loading Majas Cottage products:', err);
      setError('Kunde inte ladda produkter');
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product: MajasProduct) => {
    navigate(`/produkter/${product.slug}`);
  };

  const handleAddToCart = (product: MajasProduct) => {
    if ((product.inventory_quantity ?? 0) <= 0) {
      showToast(`${product.name} är tyvärr slutsåld just nu.`, { type: 'warning' });
      return;
    }

    addItem(product.id, 1);

    analytics.trackAddToCart(product.id, product.name, 1, Number(product.final_price ?? product.price));

    showToast(`${product.name} har lagts till i varukorgen! 🛒`, { type: 'success' });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(price);
  };

  const metaTitle = "Majas Cottage Produkter - Exklusiv Inredning & Design | Lillaflora";
  const metaDescription = "Upptäck vårt handplockade sortiment av Majas Cottage produkter. Premium inredningsdetaljer, muggar, textiler och hemkänsla i skandinavisk design. Fri frakt över 500kr.";
  const canonicalUrl = "https://lillaflora.nu/majas-cottage-produkter";

  return (
    <PageWrapper>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content="Majas Cottage, inredning, muggar, textiler, skandinavisk design, premium produkter, hemkänsla, lillaflora, kramfors" />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph tags */}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lillaflora" />

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Majas Cottage Produkter",
            "description": metaDescription,
            "url": canonicalUrl,
            "mainEntity": {
              "@type": "ItemList",
              "name": "Majas Cottage Produktkollektion",
              "numberOfItems": products.length,
              "itemListElement": products.map((product, index) => ({
                "@type": "Product",
                "position": index + 1,
                "name": product.name,
                "description": product.short_description || product.description,
                "offers": {
                  "@type": "Offer",
                  "price": product.final_price || product.price,
                  "priceCurrency": "SEK",
                  "availability": (product.inventory_quantity ?? 0) > 0
                    ? "https://schema.org/InStock"
                    : "https://schema.org/OutOfStock",
                  "seller": {
                    "@type": "Organization",
                    "name": "Lillaflora"
                  }
                }
              }))
            }
          })}
        </script>
      </Helmet>

      <Header />

      <HeroSection>
        <div className="container">
          <h1>🏠 Majas Cottage Produkter</h1>
          <p className="subtitle">
            Upptäck den vackra världen av Majas Cottage - där skandinavisk design möter funktionalitet.
            Vårt handplockade sortiment av premium inredningsdetaljer ger ditt hem den perfekta hemkänslan.
          </p>
          <a href="#products" className="cta-button">
            Utforska Kollektionen
          </a>
        </div>
      </HeroSection>

      <ContentSection>
        <div className="container">
          <h2>Majas Cottage Kollektion</h2>
          <p className="intro-text">
            Majas Cottage representerar det bästa inom skandinavisk design och kvalitet. Varje produkt är noggrant utvald för sin
            tidlösa skönhet och funktionalitet. Från eleganta muggar till mjuka textiler - här hittar du allt för att skapa
            den perfekta hemkänslan.
          </p>

          {loading && <LoadingWrapper>Laddar Majas Cottage produkter...</LoadingWrapper>}

          {error && <LoadingWrapper style={{ color: 'red' }}>{error}</LoadingWrapper>}

          {!loading && !error && (
            <>
              <div id="products">
                <ProductGrid>
                  {products.map((product) => (
                    <ProductCard key={product.id}>
                      <div className="product-image" onClick={() => handleProductClick(product)}>
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement!.innerHTML = '🏠';
                            }}
                          />
                        ) : (
                          '🏠'
                        )}
                      </div>
                      <div className="product-content">
                        <h3 className="product-name">{product.name}</h3>
                        <p className="product-description">
                          {product.short_description || product.description}
                        </p>
                        <div className="product-price">
                          <span className="current-price">
                            {formatPrice(product.final_price || product.price)}
                          </span>
                          {product.has_active_discount && product.price !== product.final_price && (
                            <>
                              <span className="original-price">
                                {formatPrice(product.price)}
                              </span>
                              <span className="discount-badge">REA</span>
                            </>
                          )}
                        </div>
                        <div className="product-actions">
                          <button
                            className="view-btn"
                            onClick={() => handleProductClick(product)}
                          >
                            Visa detaljer
                          </button>
                          <button
                            className="add-btn"
                            onClick={() => handleAddToCart(product)}
                          >
                            Köp nu 🛒
                          </button>
                        </div>
                      </div>
                    </ProductCard>
                  ))}
                </ProductGrid>
              </div>

              {products.length === 0 && (
                <LoadingWrapper>
                  Inga Majas Cottage produkter hittades för närvarande. Kom tillbaka snart för nya produkter!
                </LoadingWrapper>
              )}
            </>
          )}
        </div>
      </ContentSection>

      <AboutSection>
        <div className="container">
          <h2>Varför Majas Cottage?</h2>
          <div className="about-content">
            <div className="about-text">
              <p>
                Majas Cottage står för kvalitet, design och den perfekta hemkänslan. Varje produkt i kollektionen
                är utvald för sin unika kombination av skönhet och funktionalitet.
              </p>
              <p>
                Med fokus på skandinavisk design och naturliga material, skapar Majas Cottage produkter en
                harmonisk atmosfär i ditt hem som håller över tid.
              </p>
            </div>
            <ul className="features-list">
              <li>Premium skandinavisk design</li>
              <li>Hållbara och naturliga material</li>
              <li>Tidlös stil som aldrig går ur mode</li>
              <li>Funktionella produkter för vardagsbruk</li>
              <li>Perfekt för den moderna hemkänslan</li>
            </ul>
          </div>
        </div>
      </AboutSection>

      <CallToAction>
        <div className="container">
          <h2>Skapa Din Drömhemkänsla</h2>
          <p>
            Utforska hela vårt sortiment och hitta de perfekta Majas Cottage produkterna för ditt hem.
            Fri frakt på beställningar över 500 kr!
          </p>
          <button
            className="cta-button"
            onClick={() => navigate('/produkter')}
          >
            Se Alla Produkter →
          </button>
        </div>
      </CallToAction>

      <Footer />
    </PageWrapper>
  );
};

export default MajasCottageProducts;
